import React, { useEffect, useState, useRef } from "react";
import { getResenias, createResenia, updateResenia, deleteResenia } from "../api/resenias.js";
import { getClientes } from "../api/clientes.js";
import { getRestaurantes } from "../api/restaurantes.js";
import InputGroup from "../components/InputGroup";
import SortableHeader from "../components/SortableHeader";
import { useSortableData } from "../hooks/useSortableData.js";
import { usePagination } from "../hooks/usePagination.js";
import { useAuth } from "../context/AuthContext.js";
import Pagination from "../components/Pagination";

const ReseniasPage = () => {
    const { usuario: usuarioLogueado } = useAuth();
    const [resenias, setResenias] = useState([]);
    const [resenia, setResenia] = useState({
        calificacion: 5,
        comentario: "",
        id_cliente: "",
        id_restaurante: "",
    });
    const [filteredResenias, setFilteredResenias] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [clientes, setClientes] = useState([]);
    const [restaurantes, setRestaurantes] = useState([]);
    const inputRefs = useRef({});
    const { sortedItems: sortedResenias, sortConfig, setSortConfig } = useSortableData(filteredResenias);
    // Hook de paginación
    const { currentPage, setCurrentPage, paginatedItems: currentResenias, totalPages, itemsPerPage } = usePagination(sortedResenias, 20);
    const [errors, setErrors] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    // Cargar clientes
    useEffect(() => {
        const loadClientes = async () => {
            try {
                const data = await getClientes();
                setClientes(Array.isArray(data) ? data : data.clientes || []);
            } catch (err) {
                console.error("Error al cargar clientes:", err);
            }
        };
        loadClientes();
    }, []);

    // Cargar restaurantes
    useEffect(() => {
        const loadRestaurantes = async () => {
            try {
                const data = await getRestaurantes();
                setRestaurantes(Array.isArray(data) ? data : data.restaurantes || []);
            } catch (err) {
                console.error("Error al cargar restaurantes:", err);
            }
        };
        loadRestaurantes();
    }, []);

    // Cargar reseñas
    const loadResenias = async () => {
        try {
            const data = await getResenias();
            const lista = Array.isArray(data) ? data : data.resenias || [];
            setResenias(lista);
            setFilteredResenias(lista);
        } catch (err) {
            console.error("Error al cargar reseñas:", err);
        }
    };

    useEffect(() => {
        loadResenias();
    }, []);

    // Filtro
    useEffect(() => {
        const filtered = resenias.filter((r) =>
            Object.values(r).some((val) =>
                String(val).toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
        setFilteredResenias(filtered);
    }, [searchTerm, resenias]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setResenia({ ...resenia, [name]: value });
    };

    const resetForm = () => {
        setResenia({ 
            calificacion: 5,
            comentario: "",
            id_cliente: "",
            id_restaurante: usuarioLogueado?.isSuperAdmin ? "" : usuarioLogueado?.id_restaurante || "" 
        });
        setErrors({});
        setEditMode(false);
    };

    const validateResenia = (data) => {
        const newErrors = {};
        if (!data.calificacion || data.calificacion < 1 || data.calificacion > 5)
            newErrors.calificacion = "La calificación debe estar entre 1 y 5.";
        if (!data.comentario || data.comentario.trim() === "")
            newErrors.comentario = "Ingrese un comentario.";
        if (!data.id_cliente)
            newErrors.id_cliente = "Seleccione un cliente.";
        if (!data.id_restaurante)
            newErrors.id_restaurante = "Seleccione un restaurante.";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateResenia(resenia);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            const firstErrorField = Object.keys(validationErrors)[0];
            const fieldRef = inputRefs.current[firstErrorField];
            if (fieldRef?.scrollIntoView) {
                fieldRef.scrollIntoView({ behavior: "smooth", block: "center" });
                fieldRef.focus();
            }
            return;
        }

        try {
            const reseniaData = { ...resenia };
            
            // Si no es SuperAdmin, asignar automáticamente el id_restaurante
            if (!usuarioLogueado?.isSuperAdmin) {
                reseniaData.id_restaurante = usuarioLogueado?.id_restaurante;
            }
            
            if (editMode) {
                await updateResenia(resenia.id_resenia, reseniaData);
                alert("Reseña actualizada correctamente");
            } else {
                await createResenia(reseniaData);
                alert("Reseña creada correctamente");
            }
            resetForm();
            setModalOpen(false);
            loadResenias();
        } catch (err) {
            console.error("Error del backend:", err.response?.data || err.message);
            const backendMsg =
                err.response?.data?.error ||
                err.response?.data?.message ||
                "Error al guardar la reseña.";
            setErrors({ general: backendMsg });
            alert(backendMsg);
        }
    };

    const handleEdit = (r) => {
        setResenia(r);
        setEditMode(true);
        setModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Seguro que deseas eliminar esta reseña?")) {
            await deleteResenia(id);
            loadResenias();
        }
    };

    // Función para renderizar estrellas
    const renderStars = (calificacion) => {
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <span key={i} className={i <= calificacion ? "text-yellow-400" : "text-gray-300"}>
                    ★
                </span>
            );
        }
        return stars;
    };

    return (
        <div className="p-6 max-w-[90rem] mx-auto">
            <h1 className="text-3xl font-bold mb-6 text-center">Gestión de Reseñas</h1>

            {/* Buscador + botón */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <input type="text" placeholder="🔍 Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="border p-2 rounded w-full md:w-1/2" />
                <button onClick={() => { resetForm(); setModalOpen(true); }} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700" > ➕ Nueva Reseña </button>
            </div>

            {/* Tabla */}
            <div className="overflow-x-auto bg-white shadow-md rounded-xl w-full">
                <table className="min-w-full text-sm text-left divide-y divide-gray-200">
                    <thead className="bg-gray-100 text-gray-700 font-semibold">
                        <tr>
                            <SortableHeader label="N°" sortKey="id_resenia" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                            <SortableHeader label="Cliente" sortKey="cliente.nombre" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                            {usuarioLogueado?.isSuperAdmin && (
                                <SortableHeader label="Restaurante" sortKey="restaurante.nombre_comercial" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                            )}
                            <SortableHeader label="Calificación" sortKey="calificacion" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                            <th className="px-4 py-2">Comentario</th>
                            <SortableHeader label="Fecha" sortKey="fecha_resenia" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                            <th className="px-4 py-2 text-center">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {currentResenias.length > 0 ? (
                            currentResenias.map((r, idx) => (
                                <tr key={r.id_resenia} className="hover:bg-gray-50">
                                    <td className="px-4 py-2">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                                    <td className="px-4 py-2">{r.cliente?.nombre || "—"}</td>
                                    {usuarioLogueado?.isSuperAdmin && (
                                        <td className="px-4 py-2">
                                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                                {r.restaurante?.nombre_comercial || "Sin restaurante"}
                                            </span>
                                        </td>
                                    )}
                                    <td className="px-4 py-2 text-lg">
                                        {renderStars(r.calificacion)}
                                    </td>
                                    <td className="px-4 py-2 max-w-xs truncate">{r.comentario}</td>
                                    <td className="px-4 py-2">{r.fecha_resenia?.split("T")[0]}</td>
                                    <td className="px-4 py-2 text-center flex justify-center gap-2">
                                        <button className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500" onClick={() => handleEdit(r)} > Editar </button>
                                        <button className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600" onClick={() => handleDelete(r.id_resenia)} > Eliminar </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={usuarioLogueado?.isSuperAdmin ? "7" : "6"} className="text-center py-4 text-gray-500"> No hay reseñas registradas </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Paginación */}
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

            {/* Modal Crear / Editar */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-lg w-[70%] max-h-[90vh] overflow-y-auto px-10 py-6 relative">
                        <button onClick={() => setModalOpen(false)} className="absolute top-2 right-3 text-gray-600 hover:text-red-500 text-xl"> ✖ </button>
                        <h2 className="text-2xl font-semibold mb-6 text-center"> {editMode ? "Editar Reseña" : "Nueva Reseña"} </h2>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <InputGroup label="Cliente" error={errors.id_cliente}>
                                    <select name="id_cliente" value={resenia.id_cliente} onChange={handleChange} className="input-base" ref={(el) => (inputRefs.current["id_cliente"] = el)}>
                                        <option value="">Seleccione un cliente</option>
                                        {clientes.map((c) => (
                                            <option key={c.id_cliente} value={c.id_cliente}>
                                                {c.nombre}
                                            </option>
                                        ))}
                                    </select>
                                </InputGroup>

                                {/* Campo Restaurante - Solo visible para SuperAdmin */}
                                {usuarioLogueado?.isSuperAdmin && (
                                    <InputGroup label="Restaurante" error={errors.id_restaurante}>
                                        <select name="id_restaurante" value={resenia.id_restaurante} onChange={handleChange} className="input-base" ref={(el) => (inputRefs.current["id_restaurante"] = el)}>
                                            <option value="">Seleccione un restaurante</option>
                                            {restaurantes.map((r) => (
                                                <option key={r.id_restaurante} value={r.id_restaurante}>
                                                    {r.nombre_comercial}
                                                </option>
                                            ))}
                                        </select>
                                    </InputGroup>
                                )}

                                <InputGroup label="Calificación (1-5)" error={errors.calificacion}>
                                    <select name="calificacion" value={resenia.calificacion} onChange={handleChange} className="input-base" ref={(el) => (inputRefs.current["calificacion"] = el)}>
                                        <option value="1">⭐ 1 - Muy malo</option>
                                        <option value="2">⭐⭐ 2 - Malo</option>
                                        <option value="3">⭐⭐⭐ 3 - Regular</option>
                                        <option value="4">⭐⭐⭐⭐ 4 - Bueno</option>
                                        <option value="5">⭐⭐⭐⭐⭐ 5 - Excelente</option>
                                    </select>
                                </InputGroup>
                            </div>

                            <InputGroup label="Comentario" error={errors.comentario}>
                                <textarea 
                                    name="comentario" 
                                    value={resenia.comentario} 
                                    onChange={handleChange} 
                                    placeholder="Escriba su comentario sobre el restaurante" 
                                    className="input-base" 
                                    rows="4"
                                    ref={(el) => (inputRefs.current["comentario"] = el)}
                                />
                            </InputGroup>

                            {errors.general && ( <p className="text-red-600 text-sm text-center">{errors.general}</p> )}

                            <div className="text-center">
                                <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"> {editMode ? "Actualizar" : "Guardar"} </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReseniasPage;
